import React from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { getTime } from 'Workspace/Publications/utils';
import { timeFormats } from 'constants/constants';

import {
  List,
  ListHeader,
  ListHeaderItem,
  ListBody,
  ListItem,
  ListItemColumn,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import messages from './messages';
import { ActionButtonsWrapper, SmallButton, StatusText, EmailPearsonIdColumn, PearsonIdLine } from './styles';
import { STATUS_COMPLETE, STATUS_DENY, REQUEST_ACCESS_STATUS_VALUES } from '../../constants';

export const requestAccessShape = PropTypes.shape({
  pearsonId: PropTypes.string.isRequired,
  region: PropTypes.string,
  workspace: PropTypes.string,
  manager: PropTypes.string,
  reason: PropTypes.string,
  email: PropTypes.string,
  status: PropTypes.oneOf(REQUEST_ACCESS_STATUS_VALUES),
  createdDate: PropTypes.string,
  lastModifiedDate: PropTypes.string,
});

const sortRequestAccessList = (a, b) => ((a.workspace || '').localeCompare(b.workspace || ''));

const getStatusLabel = (status, intl) => {
  if (status === STATUS_COMPLETE) return intl.formatMessage(messages.statusComplete);
  if (status === STATUS_DENY) return intl.formatMessage(messages.statusDenied);
  return status || '—';
};

const RequestAccessList = ({ requests, onApprove, onDeny, showActions, showStatusColumn, approvingRequestId, theme }) => (
  <List>
    <ListHeader>
      <ListItemColumn colWidth="25%">
        <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderEmail)}</ListHeaderItem>
      </ListItemColumn>
      <ListItemColumn>
        <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderWorkspace)}</ListHeaderItem>
      </ListItemColumn>
      <ListItemColumn>
        <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderManager)}</ListHeaderItem>
      </ListItemColumn>
      <ListItemColumn>
        <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderReason)}</ListHeaderItem>
      </ListItemColumn>
      <ListItemColumn>
        <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderDate)}</ListHeaderItem>
      </ListItemColumn>
      {showStatusColumn && (
        <ListItemColumn>
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderStatus)}</ListHeaderItem>
        </ListItemColumn>
      )}
      {showActions && (
        <ListItemColumn>
          <ListHeaderItem>{intl.formatMessage(messages.listColumnHeaderAction)}</ListHeaderItem>
        </ListItemColumn>
      )}
    </ListHeader>
    <ListBody>
      {(requests || []).slice().sort(sortRequestAccessList).map((request) => (
        <ListItem key={`request-access-item-${request.pearsonId}`}>
          <ListItemColumn colWidth="25%">
            <EmailPearsonIdColumn>
              {request.email || '—'}
              {request?.pearsonId != null && request?.pearsonId !== '' && (
                <PearsonIdLine>{request.givenName} {request.familyName}</PearsonIdLine>
              )}
            </EmailPearsonIdColumn>
          </ListItemColumn>
          <ListItemColumn>
            {request.workspace || '—'}
          </ListItemColumn>
          <ListItemColumn>
            {request.manager || '—'}
          </ListItemColumn>
          <ListItemColumn>
            {request.reason || '—'}
          </ListItemColumn>
          {showStatusColumn && (
            <>
              <ListItemColumn>
                {getTime(request.lastModifiedDate, timeFormats.DATE_MONTH_TIME)}
              </ListItemColumn>
              <ListItemColumn>
                <StatusText isDenied={request.status === STATUS_DENY}>
                  {getStatusLabel(request.status, intl)}
                </StatusText>
              </ListItemColumn>
            </>
          )}
          {showActions && (
            <>
              <ListItemColumn>
                {getTime(request.createdDate, timeFormats.DATE_MONTH_TIME)}
              </ListItemColumn>
              <ListItemColumn>
                <ActionButtonsWrapper>
                  <SmallButton
                    onClick={() => onApprove && onApprove(request)}
                    isLoading={approvingRequestId === request.pearsonId}
                    loadable
                    disabled={!!approvingRequestId}
                  >
                    {intl.formatMessage(messages.actionApprove)}
                  </SmallButton>
                  <SmallButton disabled primary color={theme['color.redLight']} onClick={() => onDeny && onDeny(request)}>
                    {intl.formatMessage(messages.actionDeny)}
                  </SmallButton>
                </ActionButtonsWrapper>
              </ListItemColumn>
            </>
          )}
        </ListItem>
      ))}
    </ListBody>
  </List>
);

RequestAccessList.propTypes = {
  requests: PropTypes.arrayOf(requestAccessShape),
  onApprove: PropTypes.func,
  onDeny: PropTypes.func,
  showActions: PropTypes.bool,
  showStatusColumn: PropTypes.bool,
  approvingRequestId: PropTypes.string,
  theme: PropTypes.object,
};

RequestAccessList.defaultProps = {
  requests: [],
  onApprove: undefined,
  onDeny: undefined,
  showActions: true,
  showStatusColumn: false,
  approvingRequestId: null,
  theme: {},
};

export default withTheme(RequestAccessList);
